import * as apiGet from 'apiAuto/backend/paths/GetAccessoryIdV1';
import * as apiPost from 'apiAuto/backend/paths/PostAccessoryV1';
import * as apiPut from 'apiAuto/backend/paths/PutAccessoryIdV1';
import * as AccessoryState from 'components/desktop/Accessory/AccessoryState';
import { Characteristic } from 'enums/characteristic';
import { Optional } from 'functions/promiseExtensions';
import * as CategoryList from 'types/Accessory/Detail/CategoryList';
import * as Co2Label from 'types/Accessory/Detail/Co2Label';
import * as Co2List from 'types/Accessory/Detail/Co2List';
import * as Image from 'types/Accessory/Detail/Image';
import * as TagList from 'types/Accessory/Detail/TagList';
import * as UnitWeightList from 'types/Accessory/Detail/UnitWeightList';
import * as Synonyms from 'types/Synonym/Synonyms';
import * as Translation from 'types/_general/Translation';

const defaultCharacteristic: Characteristic = Characteristic.SolidNonQuantifiable;

export type Type = {
	carbonDioxide: Co2List.Type;
	carbonDioxideLabel: Co2Label.Type | null;
	carbonDioxideValue: number | null;
	categories: CategoryList.Type;
	characteristic: Characteristic;
	density: number | null;
	id: string;
	image: Image.Type | null;
	isPriceRecipeRelevant: boolean;
	namePluralTranslations: Translation.Type<string>;
	nameSingularTranslations: Translation.Type<string>;
	price: number | null;
	source: string | null;
	synonyms: Synonyms.Type;
	tags: TagList.Type;
	unitWeights: UnitWeightList.Type;
};

export function create(): Type {
	return {
		carbonDioxide: [],
		carbonDioxideLabel: null,
		carbonDioxideValue: 0,
		categories: [],
		characteristic: defaultCharacteristic,
		density: 0,
		id: '',
		image: null,
		isPriceRecipeRelevant: false,
		namePluralTranslations: Translation.create(),
		nameSingularTranslations: Translation.create(),
		price: 0,
		source: null,
		synonyms: Synonyms.create(),
		tags: [],
		unitWeights: [],
	};
}

export async function getFromApiOrCreate(
	id: string | undefined,
	currencyCode: string
): Promise<Optional<Type>> {
	if (id) {
		const response = await apiGet.callApi({
			pathParameters: { Id: id },
			queryParameters: { CurrencyCode: currencyCode },
		});
		return response.map(mapFromApi);
	}
	return Optional.Just(create());
}

export async function putToApi(id: string, request: apiPut.RequestBody) {
	return await apiPut.callApi({ pathParameters: { Id: id }, requestBody: request });
}

export async function postToApi(request: apiPost.RequestBody): Promise<string> {
	const response = await apiPost.callApi({ requestBody: request });
	return response.getOrDefault('');
}

export function mapFromApi(input: apiGet.ResponseData): Type {
	return {
		...input,
		characteristic: (input.characteristic as Characteristic) ?? defaultCharacteristic,
	};
}

export function mapToState(obj: Type, selectedCultureCode: string): AccessoryState.Type {
	return {
		...obj,
		namePlural: obj.namePluralTranslations,
		nameSingular: obj.nameSingularTranslations,
		selectedCultureCode: selectedCultureCode,
		tagIds: obj.tags.map((e) => e.id),
	};
}
